import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, distinctUntilChanged, finalize, map } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  AuthService,
  EnumValues,
  GlobalEntityDefinition,
  ModalService,
  Note,
  UserLite,
  EnumObjectExtended,
  BreadcrumbService,
} from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { Department } from '../../../models/user-master/department';
import {
  InventorySummaryItemSearch,
  ItemCategory,
  ItemMasterAvailable,
  ItemMasterFile,
  ItemMasterFilter,
} from '../../../models/item/item-master';
import { SupplyRequestEntry } from '../../../models/supply-request/supply-request-entry';

import { EnumService } from '../../../services/enum.service';
import { LocalHelperService } from '../../../services/local-helper.service';

import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { SupplyRequestProxyService } from '../../../proxies/inventory/supply-request.proxy.service';
import { DepartmentProxyService } from '../../../proxies/user-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { RequisitionFormModalComponent } from '../../common/modals/requisition-form-modal/requisition-form-modal.component';
import { InventoryType } from '../../../models/inventory/inventory-type';
import { SupplyRequestStatusEnum } from '../../../models/supply-request/supply-request-status.enum';
import { DispatchRequestProxyService } from '../../../proxies/inventory/dispatch-request.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { DispatchRequest, DispatchRequestLine } from '../../../models/dispatch-request/dispatch-request';
import {
  ExternalRequestEntry,
  ExternalRequisition,
  ExternalRequisitionSearchCommand,
} from '../../../models/order/external-requisition/external-requisition';
import { ExternalRequisitionProxyService } from '../../../proxies/order/external-requisition.proxy.service';
import { SearchBase } from '../../../models/search-base';
import { DestinationWarehouseOption, SupplyRequest } from '../../../models/supply-request/supply-request';
import { InventorySummaryProxyService } from '../../../proxies/inventory/inventory-summary.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';

@Component({
  selector: 'app-supply-request-detail',
  templateUrl: './supply-request-detail.component.html',
  styleUrl: './supply-request-detail.component.scss',
})
export class SupplyRequestDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private modalService = inject(ModalService);
  private enumService = inject(EnumService);
  private messageService = inject(MessageService);
  public localHelperService = inject(LocalHelperService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private supplyRequestProxyService = inject(SupplyRequestProxyService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private departmentProxyService = inject(DepartmentProxyService);
  private dispatchRequestProxyService = inject(DispatchRequestProxyService);
  private externalRequisitionProxyService = inject(ExternalRequisitionProxyService);
  private inventorySummaryProxyService = inject(InventorySummaryProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private itemFormSubscriptions: Subscription[] = [];
  currentLang = signal<string>('en');

  isLoading = signal(0);
  userLite: UserLite | null;
  notes: Note[] = [];

  id = signal('');
  status: FormControl<number | null> = new FormControl<number | null>(0);
  statuses: EnumValues;
  statusesIcon: EnumValues;
  statusEnumObject?: EnumObjectExtended;
  dispatchRequestStatuses: EnumValues;
  uoms: EnumValues;
  SupplyRequestStatusEnum = SupplyRequestStatusEnum;

  mainForm: FormGroup;
  filterForm: FormGroup;

  originalSupplyRequest: SupplyRequest | null = null;

  requestedItemsTableData = signal<SupplyRequestEntry[]>([]);
  searchItemsTableData = signal<ItemMasterAvailable[]>([]);
  allSearchItemsTableData = signal<ItemMasterAvailable[]>([]);

  itemsRequestForms = signal<FormGroup[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedSearchItems = signal<any | null>(null);
  existingDispatchRequests: DispatchRequest[] = [];
  existingExternalRequisition: ExternalRequisition[] = [];

  destinationWarehouseOptions: DestinationWarehouseOption[] = [];

  searchFormControl = new FormControl<string | null>(null);

  hasDataInTable = computed(() => {
    return this.itemsRequestForms().length > 0;
  });

  canSave(): boolean {
    const hasValidForms = this.itemsRequestForms().every((form) => form.valid);
    const hasItems = this.itemsRequestForms().length > 0;
    const mainFormValid = this.mainForm.valid;

    return hasValidForms && hasItems && mainFormValid;
  }

  get isSubmitted(): boolean {
    return this.status.value === SupplyRequestStatusEnum.Submitted;
  }

  get isDraft(): boolean {
    return this.status.value === SupplyRequestStatusEnum.Draft;
  }

  canCreateDispatch2 = computed(() => {
    const existId = this.id();
    const hasItems = this.itemsRequestForms().some((form) => (form.get('quantityDispatch')?.value || 0) > 0);

    const hasStatusEnable = this.status.value != null && this.status.value === SupplyRequestStatusEnum.Submitted;

    return hasItems && existId && hasStatusEnable;
  });

  get canCreateDispatch() {
    const existId = this.id();
    const hasItems = this.itemsRequestForms().some((form) => (form.get('quantityDispatch')?.value || 0) > 0);

    const hasStatusEnable = this.status.value != null && this.status.value === SupplyRequestStatusEnum.Submitted;

    return hasItems && existId && hasStatusEnable;
  }

  get canCreateRequisition() {
    const existId = this.id();
    const hasItems = this.itemsRequestForms().some((form) => (form.get('quantityForRequisition')?.value || 0) > 0);

    const hasStatusEnable =
      this.status.value != null &&
      (this.status.value === SupplyRequestStatusEnum.Submitted || this.status.value === SupplyRequestStatusEnum.Draft);

    return hasItems && existId && hasStatusEnable;
  }

  canCreateRequisition2 = computed(() => {
    const existId = this.id();
    const hasItems = this.itemsRequestForms().some((form) => (form.get('quantityDispatch')?.value || 0) > 0);

    const hasStatusEnable =
      this.status.value != null &&
      (this.status.value === SupplyRequestStatusEnum.Submitted || this.status.value === SupplyRequestStatusEnum.Draft);

    return hasItems && existId && hasStatusEnable;
  });

  canCreate = computed(() => AuthService.hasRole(UserGroup.SupplyRequestAdmin));

  constructor() {
    this.id.set(this.route.snapshot.queryParamMap.get('id') || '');
    this.userLite = this.userAuthenticationService.userLite;
    this.statusEnumObject = this.enumService.getEnum('SupplyRequestStatus', 'inventory');
    this.statuses = this.statusEnumObject?.values ?? {};
    this.statusesIcon = this.statusEnumObject?.valuesEnglish ?? {};
    this.dispatchRequestStatuses = this.enumService.getEnum('StatusDispatchRequest', 'inventory').values;
    this.uoms = this.enumService.getEnum('UnitOfMeasure', 'item').values;

    this.mainForm = this.initializeMainForm();
    this.filterForm = this.initializeFilterForm();

    this.currentLang.set(this.translateService.currentLang || 'en');

    this.setupFormValueSync();
  }

  get availableStatuses(): EnumValues {
    const status = this.status.value;
    const statusIntToInclude: number[] = [];
    if (this.id()) {
      statusIntToInclude.push(SupplyRequestStatusEnum.Voided);
    }
    if (status === SupplyRequestStatusEnum.Draft) {
      statusIntToInclude.push(SupplyRequestStatusEnum.Submitted);
    }
    return Object.fromEntries(Object.entries(this.statuses).filter(([key]) => statusIntToInclude.includes(Number(key))));
  }

  private setupFormValueSync() {
    this.status.valueChanges.subscribe((value) => {
      const isDraft = value === SupplyRequestStatusEnum.Draft;
      const departmentControl = this.mainForm.get('departmentReference');

      if (this.originalSupplyRequest?.status !== value) {
        if (value === SupplyRequestStatusEnum.Submitted) {
          this.updateToSubmittedState();
        } else if (value === SupplyRequestStatusEnum.Voided) {
          this.updateToVoidState();
        }
      }

      if (isDraft) {
        departmentControl?.enable();
      } else {
        departmentControl?.disable();
      }
    });
  }

  private updateToSubmittedState() {
    //check that all entries have desired quantity >= 0.01
    //after check that department is selected and due date is set
    //if this passes, creat ea confirmation saying taht their request will be submitted
    // and can't be edited after submission
    const allEntriesValid = this.itemsRequestForms().every((form) => (form.get('desiredQuantity')?.value ?? 0) >= 0.01);
    const departmentSelected = !!this.mainForm.get('departmentReference')?.value;

    if (allEntriesValid && departmentSelected) {
      const titleMessage = this.translateService.instant('SubmitRequest_Confirm_Title');
      const confirmationMessage = this.translateService.instant('SubmitRequest_Confirm_Body');

      this.confirmationService.confirm({
        key: 'main',
        header: titleMessage,
        message: confirmationMessage,
        acceptLabel: this.translateService.instant('Common_Confirm'),
        rejectLabel: this.translateService.instant('Common_Cancel'),
        rejectButtonProps: { severity: 'secondary', outlined: true },
        accept: () => {
          this.status.setValue(SupplyRequestStatusEnum.Submitted, { emitEvent: false });
          this.onSave();
        },
      });
    } else {
      GlobalMessageUtil.warn(
        'Warning',
        'Please ensure all items have a desired quantity of at least 0.01, a department is selected, and a due date is set before submitting.',
      );
      if (this.id()) {
        this.status.setValue(this.originalSupplyRequest?.status || SupplyRequestStatusEnum.Draft, { emitEvent: true });
      } else {
        this.status.setValue(SupplyRequestStatusEnum.Draft, { emitEvent: true });
      }
    }
  }

  private updateToVoidState() {
    if (!this.originalSupplyRequest) return;
    const titleMessage = this.translateService.instant('VoidRequest_Confirm_Title');
    const confirmationMessage = this.translateService.instant('VoidRequest_Confirm_Body');

    if (this.existingDispatchRequests.length > 0) {
      GlobalMessageUtil.error('Error', 'Cannot void this request because there are existing dispatch requests associated with it.');
      this.status.setValue(this.originalSupplyRequest.status, { emitEvent: false });
      return;
    }

    if (this.existingExternalRequisition.length > 0) {
      GlobalMessageUtil.error('Error', 'Cannot void this request because there are existing external requisitions associated with it.');
      this.status.setValue(this.originalSupplyRequest.status, { emitEvent: false });
      return;
    }

    this.confirmationService.confirm({
      key: 'main',
      header: titleMessage,
      message: confirmationMessage,
      acceptLabel: this.translateService.instant('Common_Confirm'),
      rejectLabel: this.translateService.instant('Common_Cancel'),
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        this.status.setValue(SupplyRequestStatusEnum.Voided, { emitEvent: false });
        this.saveVoidedStatus();
      },
    });
  }

  private getExistingRelatedEntities() {
    if (!this.id()) return;
    const searchCommand: SearchBase = {
      initiator: {
        reference: this.id(),
        entityDefinitionCode: GlobalEntityDefinition.SupplyRequest,
      },
    };

    const externalSearchCommand: ExternalRequisitionSearchCommand = {
      entityKey: {
        reference: this.id(),
        entityDefinitionCode: GlobalEntityDefinition.SupplyRequest,
      },
    };

    this.startLoading();
    this.startLoading();
    this.dispatchRequestProxyService.search(searchCommand).subscribe({
      next: (response) => {
        this.stopLoading();
        if (response.success && response.data) {
          this.existingDispatchRequests = response.data.items ?? [];
        }
      },
      error: () => this.stopLoading(),
    });

    this.externalRequisitionProxyService.search(externalSearchCommand, 1, 9999).subscribe({
      next: (response) => {
        this.stopLoading();
        if (response.success && response.data) {
          this.existingExternalRequisition = response.data.items;
        }
      },
      error: () => this.stopLoading(),
    });
  }

  private initializeMainForm(): FormGroup {
    return new FormGroup({
      id: new FormControl<string | null>(null),
      createdDate: new FormControl<Date | string | null>({ value: new Date(), disabled: true }),
      departmentReference: new FormControl<string | null>(null, Validators.required),
      locationCode: new FormControl<string | null>('NT.WH-CON-1', Validators.required),
    });
  }

  private initializeFilterForm(): FormGroup {
    return new FormGroup({
      name: new FormControl<string | null>(null),
      itemCode: new FormControl<string | null>(null),
      description: new FormControl<string | null>(null),
      itemCategoryCode: new FormControl<ItemCategory | null>(null),
      itemLowestSubCategoryCode: new FormControl<ItemCategory | null>(null),
      inventoryTypeReference: new FormControl<InventoryType | null>(null),
      itemCategoryReference: new FormControl<string | null>(null),
    });
  }

  ngOnInit(): void {
    this.loadConsumableWarehouses();

    if (this.id()) {
      this.loadById();
      this.getExistingRelatedEntities();
    } else {
      this.breadcrumbService.setCurrent('Common_New');
      this.status.setValue(SupplyRequestStatusEnum.Draft);
      if (!this.canCreate()) {
        this.mainForm.disable();
      }
    }
  }

  startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  stopLoading(): void {
    this.isLoading.update((val) => Math.max(0, val - 1));
  }

  cancel() {
    this.router.navigate(['/supply-request']);
  }

  private loadConsumableWarehouses(): void {
    this.locationProxyService.getConsumableWarehouses().subscribe({
      next: (response) => {
        if (response.data) {
          this.destinationWarehouseOptions = response.data.map((w) => ({
            code: w.locationCode,
            label: this.localHelperService.getLocalizedValue(w.fullName ?? w.name) + ` (${w.locationCode})`,
          }));
        }
      },
    });
  }

  public fetchDepartments = (query: string): Observable<Department[]> =>
    this.departmentProxyService
      .searchByMember(this.userLite?.username || '')
      .pipe(
        map((response) =>
          (response.data ?? []).filter((dep) =>
            this.localHelperService.getLocalizedValue(dep.name)?.toLowerCase().includes(query.toLowerCase()),
          ),
        ),
      );

  getDepartmentLabel = (department: Department): string => {
    return this.localHelperService.getLocalizedValue(department.name);
  };

  getRequisitionStatusSeverity(statusReference?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (!statusReference) return 'secondary';
    // You can adjust these mappings based on your actual status values
    const statusLower = statusReference.toLowerCase();
    if (statusLower.includes('approved') || statusLower.includes('complete')) return 'success';
    if (statusLower.includes('draft') || statusLower.includes('pending')) return 'warn';
    if (statusLower.includes('rejected') || statusLower.includes('cancelled')) return 'danger';
    return 'info';
  }

  private loadById() {
    this.startLoading();
    this.supplyRequestProxyService
      .getById(this.id())
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          console.log(response);
          this.originalSupplyRequest = response.data;
          this.breadcrumbService.setCurrent(response.data?.number);

          if (response.data) {
            this.notes = response.data.notes || [];
            this.mainForm.patchValue({
              ...response.data,
              createdDate: response.data.createdDate ? new Date(response.data.createdDate) : null,
              locationCode: response.data.locationCode || 'NT.WH-CON-1',
            });
            const department: Partial<Department> = {
              id: response.data.departmentReference,
              name: response.data.departmentName,
            };
            this.mainForm.get('departmentReference')?.setValue(department);

            // Disable destination selector if already submitted
            if (response.data.status !== SupplyRequestStatusEnum.Draft) {
              this.mainForm.get('locationCode')?.disable();
            }

            this.status.setValue(response.data.status);

            response.data.entries.forEach((element) => {
              const newForm = this.initializeItemToRequestForm();
              newForm.patchValue({
                ...element,
                quantityDispatch:
                  element.desiredQuantity <= element.stockBalanceAtTimeOfRequest
                    ? element.desiredQuantity
                    : element.stockBalanceAtTimeOfRequest,
              });
              if (this.isSubmitted || !this.canCreate()) {
                //disable desired quantity if the request is already submitted or user is viewer
                newForm.get('desiredQuantity')?.disable();
                newForm.get('comment')?.disable();
              }

              this.itemsRequestForms.update((forms) => [...forms, newForm]);
            });
          }
        },
      });
  }

  private initializeItemToRequestForm(): FormGroup {
    return new FormGroup({
      lineId: new FormControl<string | null>(null),
      itemCode: new FormControl<string | null>(null),
      itemDisplayName: new FormControl<string | null>(null),
      comment: new FormControl<string | null>(null),
      stockBalanceAtTimeOfRequest: new FormControl<number | null>({ value: null, disabled: true }),
      desiredQuantity: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
      quantityForRequisition: new FormControl<number | null>({ value: null, disabled: true }),
      quantityDispatch: new FormControl<number | null>({ value: null, disabled: true }),
      uom: new FormControl<number | null>(null),
    });
  }

  private convertItemMasterToRequestEntry(item: ItemMasterAvailable): SupplyRequestEntry {
    return {
      lineId: item.id || '',
      itemCode: item.itemCode || '',
      itemDisplayName: item.itemDisplayName,
      desiredQuantity: 1,
      stockBalanceAtTimeOfRequest: item.availableQuantity || 0,
      quantityForRequisition: 0,
      comment: '',
      uom: item.unitOfMeasure || 0,
    };
  }

  applyFiltersSearchGlobal() {
    const query = (this.searchFormControl.value || '').trim();

    if (!query) {
      this.searchItemsTableData.set([]);
      return;
    }

    // Parse locationCode (format: "NT.WH-CON-1" or "NT.WH-LD-1") to get siteCode and warehouseCode
    const selectedLocationCode = this.mainForm.get('locationCode')?.value || 'NT.WH-CON-1';
    const [siteCode, warehouseCode] = selectedLocationCode.split('.');

    const inventorySummaryItemSearch: InventorySummaryItemSearch = {
      itemSearch: { itemCode: query, name: query },
      location: { siteCode: siteCode || 'NT', warehouseCode: warehouseCode || 'WH-CON-1' },
    };

    this.inventorySummaryProxyService.itemSearch(inventorySummaryItemSearch, true, false, 1, 100).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.searchItemsTableData.set(response.data.items);
        }
      },
    });
  }

  public itemMasterStrict(item: unknown): ItemMasterAvailable {
    return item as ItemMasterAvailable;
  }

  public externalRequisitionStrict(item: unknown): ExternalRequisition {
    return item as ExternalRequisition;
  }

  public requisitionEntryStrict(item: unknown): ExternalRequestEntry {
    return item as ExternalRequestEntry;
  }

  public dispatchEntryStrict(item: unknown): DispatchRequestLine {
    return item as DispatchRequestLine;
  }

  private applyFiltersSearch() {
    this.startLoading();

    const filterData: ItemMasterFilter = {
      name: this.filterForm.get('name')?.value || '',
      itemCode: this.filterForm.get('itemCode')?.value || '',
      description: this.filterForm.get('description')?.value || '',
      itemCategoryCode: this.filterForm.get('itemCategoryCode')?.value?.code || '',
      itemLowestSubCategoryCode: this.filterForm.get('itemLowestSubCategoryCode')?.value?.code || '',
      inventoryTypeReference: this.filterForm.get('inventoryTypeReference')?.value?.id || '',
      itemCategoryReference: this.filterForm.get('itemCategoryReference')?.value || '',
    };

    this.itemMasterProxyService.search(filterData).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.searchItemsTableData.set(response.data?.items || []);
        }
        this.stopLoading();
      },
    });
  }

  public getMainImageUrl(files: ItemMasterFile[]): string | null {
    const mainFile = files?.find((file) => file.isMain);
    return mainFile ? mainFile.presignedUrl : null;
  }

  public openImageInNewTab(url: string) {
    window.open(url, '_blank');
  }

  addItemToRequest(item: ItemMasterAvailable) {
    const selectedItem = item;

    if (!selectedItem) return;

    if (this.itemsRequestForms().some((f) => f.get('itemCode')?.value === selectedItem.itemCode)) {
      return;
    }

    const newForm = this.initializeItemToRequestForm();
    newForm.patchValue(this.convertItemMasterToRequestEntry(selectedItem));

    this.itemsRequestForms.update((forms) => [...forms, newForm]);
    this.searchItemsTableData.update((items) => items.filter((i) => i.itemCode !== selectedItem.itemCode));

    this.setupFormItemToRequestValueSync();
  }

  removeItemFromRequest(itemCode: string) {
    console.log('Removing item with code:', itemCode);
    const itemToRemove = this.itemsRequestForms().find((f) => f.get('itemCode')?.value === itemCode);
    if (!itemToRemove) return;

    this.itemsRequestForms.update((forms) => forms.filter((f) => f.get('itemCode')?.value !== itemCode));
  }

  private setupFormItemToRequestValueSync() {
    this.itemFormSubscriptions.forEach((sub) => sub.unsubscribe());
    this.itemFormSubscriptions = [];

    const forms = this.itemsRequestForms();
    forms.forEach((form) => {
      const sub = form
        .get('desiredQuantity')
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe((value) => {
          const stockBalanceAtTimeOfRequest = form.get('stockBalanceAtTimeOfRequest')?.value || 0;
          const desiredQuantity = value ?? 0;
          form
            .get('quantityDispatch')
            ?.setValue(desiredQuantity <= stockBalanceAtTimeOfRequest ? desiredQuantity : stockBalanceAtTimeOfRequest, {
              emitEvent: false,
            });

          form
            .get('quantityForRequisition')
            ?.setValue(desiredQuantity <= stockBalanceAtTimeOfRequest ? 0 : desiredQuantity - stockBalanceAtTimeOfRequest, {
              emitEvent: false,
            });
          form.updateValueAndValidity();
        });
      if (sub) this.itemFormSubscriptions.push(sub);
    });
  }

  onCreateDispatch() {
    if (this.canCreateDispatch) {
      const titleMessage = this.translateService.instant('CreateDispatch_Confirm_Title');
      const confirmationMessage = this.translateService.instant('CreateDispatch_Confirm_Body');

      this.confirmationService.confirm({
        key: 'main',
        header: titleMessage,
        message: confirmationMessage,
        acceptLabel: this.translateService.instant('Common_Confirm'),
        rejectLabel: this.translateService.instant('Common_Cancel'),
        rejectButtonProps: { severity: 'secondary', outlined: true },
        accept: () => {
          this.startLoading();

          const request = {
            reference: this.id(),
            entityDefinitionCode: GlobalEntityDefinition.SupplyRequest,
          };

          this.dispatchRequestProxyService
            .createForEntity(request)
            .pipe(
              finalize(() => {
                this.stopLoading();
              }),
            )
            .subscribe({
              next: (result) => {
                if (result.success) {
                  GlobalMessageUtil.success('Success', 'Dispatch Request created successfully');
                  this.getExistingRelatedEntities();
                }
              },
            });
        },
        reject: () => {
          this.status.setValue(this.status!.value, { emitEvent: false });
        },
      });
    }
  }

  onPrintDispatch(dispatchId: string) {
    this.startLoading();

    this.dispatchRequestProxyService
      .downloadReport(dispatchId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `DIS-${dispatchId}.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          GlobalMessageUtil.success('Success', 'Dispatch Request printed successfully');
        },
        error: (error) => {
          console.error('Error downloading report:', error);
        },
      });
  }

  openRequisitionFormModal() {
    this.modalService
      .open(
        RequisitionFormModalComponent,
        {},
        {
          supplyRequestReference: this.id(),
          requestedItemsTableData: this.getItemsToRequisition(),
        },
      )
      .subscribe((result) => {
        if (result.action === 'success') {
          this.getExistingRelatedEntities();
        }
      });
  }

  getItemsToRequisition() {
    return this.itemsRequestForms()
      .filter((form) => (form.get('quantityForRequisition')?.value || 0) > 0)
      .map((form) => form.getRawValue());
  }

  saveVoidedStatus() {
    if (this.status.value === SupplyRequestStatusEnum.Voided) {
      this.supplyRequestProxyService.updateStatus(this.id(), SupplyRequestStatusEnum.Submitted).subscribe({
        next: (response) => {
          if (response.success) {
            GlobalMessageUtil.success('Success', 'Status updated to Submitted');
          } else {
            GlobalMessageUtil.error('Error', 'Failed to update status to Submitted');
          }
        },
        error: () => {
          this.status.setValue(SupplyRequestStatusEnum.Voided, { emitEvent: false });
        },
      });
    }
  }

  onSave(resetStatusToDraftIfFail = false) {
    if (!this.canSave()) return;
    this.startLoading();

    this.mainForm.enable();
    const locationCode = this.mainForm.get('locationCode')?.value || 'NT.WH-CON-1';
    const request = {
      ...this.mainForm.value,
      entries: this.itemsRequestForms().map((form) => form.getRawValue()),
      locationCode: locationCode,
      notes: this.notes,
      statusHistory: [],
    };

    request.status = this.status.value;

    console.log(request);

    if (!this.id()) {
      request.status = SupplyRequestStatusEnum.Submitted;

      this.supplyRequestProxyService
        .create(request)
        .pipe(
          finalize(() => {
            this.stopLoading();
          }),
        )
        .subscribe({
          next: (response) => {
            console.log(response);
            if (response.success) {
              this.router.navigate(['/supply-request']);
            }
          },
          error: () => {
            if (resetStatusToDraftIfFail) {
              this.status.setValue(SupplyRequestStatusEnum.Draft, { emitEvent: false });
            }
          },
        });
    } else {
      this.supplyRequestProxyService
        .update(this.id(), request)
        .pipe(
          finalize(() => {
            this.stopLoading();
          }),
        )
        .subscribe({
          next: (response) => {
            console.log(response);
            if (response.success) {
              this.router.navigate(['/supply-request']);
            }
          },
          error: () => {
            if (resetStatusToDraftIfFail) {
              this.status.setValue(SupplyRequestStatusEnum.Draft, { emitEvent: false });
            }
          },
        });
    }
  }

  onAddNote(note: Note): void {
    // If save button is hidden (not in Draft status), immediately save the note
    if (!this.isDraft) {
      const supplyRequestId = this.id();
      if (supplyRequestId) {
        this.isLoading.update((val) => val + 1);
        this.supplyRequestProxyService.insertNote(supplyRequestId, note).subscribe({
          next: (response) => {
            this.isLoading.update((val) => Math.max(0, val - 1));
            if (response.success) {
              GlobalMessageUtil.success('Success', 'Note added successfully');
            }
          },
          error: (error) => {
            this.isLoading.update((val) => Math.max(0, val - 1));
            console.error('Error adding note:', error);
            // Remove the note from local array on error
            this.notes = this.notes.filter((n) => n !== note);
          },
        });
      }
    }
    // If in Draft status, the note will be saved when the user clicks the Save button
  }
}
